// src/data/db.js  —  Firebase Firestore version
// Drop-in replacement for the localStorage version.
// AppContext and all pages/components are UNCHANGED.
import {
  collection, doc,
  getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  query, orderBy, serverTimestamp,
} from 'firebase/firestore';

import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged,
  updatePassword, reauthenticateWithCredential, EmailAuthProvider,
} from 'firebase/auth';

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../lib/firebase';

const COL = {
  PACKAGES:     'packages',
  ITINERARIES:  'itineraries',
  ENQUIRIES:    'enquiries',
  DESTINATIONS: 'destinations',
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const Auth = {
  init() {
    return new Promise(resolve => {
      const unsub = onAuthStateChanged(auth, user => { unsub(); resolve(!!user); });
    });
  },
  onAuthChange(cb) { return onAuthStateChanged(auth, user => cb(!!user)); },
  async login(email, password) {
    try { await signInWithEmailAndPassword(auth, email, password); return true; }
    catch (e) { console.error('login:', e.code); return false; }
  },
  async logout() { await signOut(auth); },
  async changePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) return { ok: false, error: 'Not logged in' };
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPassword);
      return { ok: true };
    } catch (e) {
      const msg = e.code === 'auth/wrong-password' ? 'Current password is incorrect'
                : e.code === 'auth/weak-password'  ? 'New password must be at least 6 characters'
                : e.message;
      return { ok: false, error: msg };
    }
  },
};

// ── Packages ──────────────────────────────────────────────────────────────────
export const PackagesDB = {
  async getAll() {
    try {
      const snap = await getDocs(query(collection(db, COL.PACKAGES), orderBy('createdAt','desc')));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch(e) { console.error('PackagesDB.getAll:', e.message); return SEED_PACKAGES; }
  },
  async getById(id) {
    try {
      const snap = await getDoc(doc(db, COL.PACKAGES, id));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch(e) { return null; }
  },
  async create(data) {
    try {
      const payload = { ...sanitizePkg(data), createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
      const r = await addDoc(collection(db, COL.PACKAGES), payload);
      return { id: r.id, ...payload, createdAt: new Date().toISOString() };
    } catch(e) { console.error('PackagesDB.create:', e.message); return null; }
  },
  async update(id, data) {
    try {
      const payload = { ...sanitizePkg(data), updatedAt: serverTimestamp() };
      await updateDoc(doc(db, COL.PACKAGES, id), payload);
      return { id, ...payload };
    } catch(e) { console.error('PackagesDB.update:', e.message); return null; }
  },
  async delete(id) {
    try { await deleteDoc(doc(db, COL.PACKAGES, id)); }
    catch(e) { console.error('PackagesDB.delete:', e.message); }
  },
};

// ── Itineraries ───────────────────────────────────────────────────────────────
export const ItinerariesDB = {
  async getAll() {
    try {
      const snap = await getDocs(query(collection(db, COL.ITINERARIES), orderBy('createdAt','desc')));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch(e) { console.error('ItinerariesDB.getAll:', e.message); return []; }
  },
  async getById(id) {
    try {
      const snap = await getDoc(doc(db, COL.ITINERARIES, id));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch(e) { return null; }
  },
  async create(data) {
    try {
      const payload = { ...data, inclusions: data.inclusions||[], exclusions: data.exclusions||[], days: data.days||[], notes: data.notes||'', createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
      const r = await addDoc(collection(db, COL.ITINERARIES), payload);
      return { id: r.id, ...payload, createdAt: new Date().toISOString() };
    } catch(e) { console.error('ItinerariesDB.create:', e.message); return null; }
  },
  async update(id, data) {
    try {
      const payload = { ...data, updatedAt: serverTimestamp() };
      await updateDoc(doc(db, COL.ITINERARIES, id), payload);
      return { id, ...payload };
    } catch(e) { console.error('ItinerariesDB.update:', e.message); return null; }
  },
  async delete(id) {
    try { await deleteDoc(doc(db, COL.ITINERARIES, id)); }
    catch(e) { console.error('ItinerariesDB.delete:', e.message); }
  },
};

// ── Enquiries ─────────────────────────────────────────────────────────────────
export const EnquiriesDB = {
  async getAll() {
    try {
      const snap = await getDocs(query(collection(db, COL.ENQUIRIES), orderBy('createdAt','desc')));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch(e) { console.error('EnquiriesDB.getAll:', e.message); return []; }
  },
  async create(data) {
    try {
      const payload = { name: data.name||'', email: data.email||'', phone: data.phone||'', packageId: data.packageId||null, packageTitle: data.packageTitle||'', packagePrice: data.packagePrice||null, travelers: data.travelers||'2', travelMonth: data.travelMonth||'', message: data.message||'', source: data.source||'website', status: 'new', createdAt: serverTimestamp() };
      const r = await addDoc(collection(db, COL.ENQUIRIES), payload);
      return { id: r.id, ...payload, createdAt: new Date().toISOString() };
    } catch(e) { console.error('EnquiriesDB.create:', e.message); return null; }
  },
  async updateStatus(id, status) {
    try { await updateDoc(doc(db, COL.ENQUIRIES, id), { status }); }
    catch(e) { console.error('EnquiriesDB.updateStatus:', e.message); }
  },
  async delete(id) {
    try { await deleteDoc(doc(db, COL.ENQUIRIES, id)); }
    catch(e) { console.error('EnquiriesDB.delete:', e.message); }
  },
};

// ── Destinations ──────────────────────────────────────────────────────────────
export const DestinationsDB = {
  async getAll() {
    try {
      const snap = await getDocs(query(collection(db, COL.DESTINATIONS), orderBy('name','asc')));
      if (snap.empty) return SEED_DESTINATIONS;
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch(e) { console.error('DestinationsDB.getAll:', e.message); return SEED_DESTINATIONS; }
  },
  async create(data) {
    try {
      const payload = { ...data, createdAt: serverTimestamp() };
      const r = await addDoc(collection(db, COL.DESTINATIONS), payload);
      return { id: r.id, ...data };
    } catch(e) { console.error('DestinationsDB.create:', e.message); return null; }
  },
  async delete(id) {
    try { await deleteDoc(doc(db, COL.DESTINATIONS, id)); }
    catch(e) { console.error('DestinationsDB.delete:', e.message); }
  },
};

// ── Image Storage ─────────────────────────────────────────────────────────────
export const StorageDB = {
  async uploadImage(file, folder = 'packages') {
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const path = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      return await getDownloadURL(fileRef);
    } catch(e) { console.error('StorageDB.uploadImage:', e.message); return null; }
  },
  async deleteImage(url) {
    try { await deleteObject(ref(storage, url)); }
    catch(e) { console.warn('StorageDB.deleteImage:', e.message); }
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function sanitizePkg(pkg) {
  return {
    title: pkg.title||'', destination: pkg.destination||'', duration: pkg.duration||'',
    price: parseInt(pkg.price)||0, originalPrice: pkg.originalPrice ? parseInt(pkg.originalPrice) : null,
    image: pkg.image||'', theme: pkg.theme||'Domestic Holiday', description: pkg.description||'',
    highlights: toArr(pkg.highlights), inclusions: toArr(pkg.inclusions),
    amenities: pkg.amenities||{}, available: pkg.available!==false, itineraryId: pkg.itineraryId||null,
  };
}
function toArr(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v==='string') return v.split(',').map(s=>s.trim()).filter(Boolean);
  return [];
}

// ── Seed data (fallback when Firestore is empty or offline) ───────────────────
const SEED_PACKAGES = [
  { id:'pkg_seed_001', available:true, createdAt:'2024-01-01T00:00:00Z', title:'Best of Andaman', destination:'Andaman and Nicobar Islands', duration:'6N/7D', price:28999, originalPrice:35000, image:'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=700&q=80', theme:'Beach Holiday', highlights:['Port Blair','Havelock Island','Neil Island'], inclusions:['Hotel Stay','Daily Breakfast','Ferry Transfers','Sightseeing'], amenities:{hotel:true,flight:false,meals:true,transfer:true,ferry:true}, description:'Explore the pristine beaches of Andaman.', itineraryId:null },
  { id:'pkg_seed_002', available:true, createdAt:'2024-01-02T00:00:00Z', title:'Amazing Ladakh', destination:'Ladakh', duration:'4N/5D', price:22999, originalPrice:28000, image:'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=700&q=80', theme:'Adventure', highlights:['Leh','Pangong Lake','Nubra Valley'], inclusions:['Hotel Stay','Breakfast','Transport','Guide'], amenities:{hotel:true,flight:false,meals:false,transfer:true,ferry:false}, description:'Experience the surreal landscapes of Ladakh.', itineraryId:null },
  { id:'pkg_seed_003', available:true, createdAt:'2024-01-03T00:00:00Z', title:'Bali – Kuta & Ubud', destination:'Bali, Indonesia', duration:'6N/7D', price:45999, originalPrice:55000, image:'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=700&q=80', theme:'Honeymoon', highlights:['Kuta Beach','Ubud Rice Terraces','Tanah Lot'], inclusions:['Hotel','Breakfast','Airport Transfer','Sightseeing'], amenities:{hotel:true,flight:false,meals:false,transfer:true,ferry:false}, description:'Romantic Bali getaway.', itineraryId:null },
  { id:'pkg_seed_004', available:true, createdAt:'2024-01-04T00:00:00Z', title:'Royal Rajasthan', destination:'Rajasthan', duration:'7N/8D', price:32000, originalPrice:40000, image:'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=700&q=80', theme:'Domestic Holiday', highlights:['Jaipur','Jodhpur','Udaipur','Jaisalmer'], inclusions:['Hotel','Breakfast & Dinner','AC Transport','Guide'], amenities:{hotel:true,flight:false,meals:true,transfer:true,ferry:false}, description:'Journey through the Land of Kings.', itineraryId:null },
];
const SEED_DESTINATIONS = [
  { id:'d01', name:'Kerala',      type:'domestic',      image:'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400&q=75', packages:1 },
  { id:'d02', name:'Meghalaya',   type:'domestic',      image:'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=400&q=75', packages:2 },
  { id:'d03', name:'Goa',         type:'domestic',      image:'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=75', packages:2 },
  { id:'d04', name:'Rajasthan',   type:'domestic',      image:'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=400&q=75', packages:3 },
  { id:'d05', name:'Ladakh',      type:'domestic',      image:'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=400&q=75', packages:3 },
  { id:'d06', name:'Andaman',     type:'domestic',      image:'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=75', packages:4 },
  { id:'d07', name:'Sikkim',      type:'domestic',      image:'https://images.unsplash.com/photo-1601841842850-b9b36e7e32c3?w=400&q=75', packages:2 },
  { id:'d08', name:'Bali',        type:'international', image:'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=400&q=75', packages:3 },
  { id:'d09', name:'Thailand',    type:'international', image:'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=400&q=75', packages:2 },
];
